import {ProvisionSimulationState} from '../../enum/provision-simulation-state.enum';

/**
 * The two presentation rules the simulation screen's components SHARE — kept in one place because
 * two hand-written copies of a rule drift silently: both keep returning a value, just a different
 * one. Formatting of figures lives next door in `cp-simulation-figure-format.ts`; captions of enum
 * values in `enum/provision-simulation-technical-label.ts`.
 */

/**
 * Inline colour of a chip, taken from the backend's own `@UiEnumValueInfo` tokens
 * (`backgroundColor` / `color` of a `DtoEnumInfo`, e.g. `green-100` / `green-800`).
 *
 * Inline on purpose: `severity` is inert in this application — the global PrimeNG overrides colour
 * EVERY `p-tag` unqualified on `--primary-800` and beat PrimeNG's own severity classes — while an
 * inline style beats a stylesheet rule that carries no `!important`. Returns `null` when the
 * response carried no token at all, so the chip keeps the application's default styling rather
 * than a colour invented here.
 */
export function provisionSimulationChipStyle(backgroundColor: string, color: string): { [key: string]: string } {

    if (!backgroundColor && !color) {
        return null;
    }

    const style: { [key: string]: string } = {};

    if (backgroundColor) {
        style['background-color'] = 'var(--' + backgroundColor + ')';
    }
    if (color) {
        style['color'] = 'var(--' + color + ')';
    }

    return style;
}

/**
 * The state of a simulation as the FE enum constant, or `null` when it cannot be resolved.
 *
 * The wire sends the ordinal, which IS the constant. A backend older than that sends the enum
 * NAME, which is resolved through the enum mirror by name — the only place a raw string is
 * touched, and only to turn it into the constant every branch compares against. An unknown name
 * yields `null`, never a guess.
 */
export function provisionSimulationResolvedState(
    state: ProvisionSimulationState | number | string): ProvisionSimulationState {

    if (typeof state === 'number') {
        return state;
    }

    if (typeof state === 'string') {
        const ordinal: unknown = ProvisionSimulationState[state as keyof typeof ProvisionSimulationState];
        return typeof ordinal === 'number' ? ordinal : null;
    }

    return null;
}
